<template>
    <div class="flex justify-between pb-2" ref="carouselContainer">
        <div
            class="grow shrink basis-0 flex-col justify-end items-start gap-1flex"
        >
            <h2
                v-if="title"
                class="text-slate-800/95 dark:text-slate-100/95 text-lg font-medium leading-7"
            >
                {{ title }}
            </h2>
            <div
                v-if="description"
                class="text-slate-500 dark:text-slate-400 text-sm font-normal pb-2"
            >
                {{ description }}
            </div>
        </div>
        <Link
            v-if="seeAllUrl"
            class="flex items-end rounded text-slate-800 dark:text-slate-100 text-sm font-medium hover:text-slate-500 dark:hover:text-slate-400 active:text-slate-500 dark:active:text-slate-400 focus:outline-none"
            :href="seeAllUrl"
            role="button"
            @click="$emit('seeAllClicked')"
        >
            See all
        </Link>
    </div>

    <Carousel v-bind="config">
        <template v-slot:default>
            <Slide v-for="card in cards" :key="card.id ? card.id : card.name">
                <slot name="carouselCard" :card="card"></slot>
            </Slide>
        </template>
        <template #addons>
            <Navigation v-if="showCarousel" />
            <Pagination v-if="pagination" />
        </template>
    </Carousel>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { Carousel, Slide, Navigation, Pagination } from 'vue3-carousel';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import { Link } from '@inertiajs/vue3';

export default {
    components: {
        Carousel,
        Navigation,
        Slide,
        Pagination,
        Link,
    },
    emits: ['seeAllClicked'],
    props: {
        seeAllUrl: String,
        numCards: {
            type: Number,
            required: true,
        },
        title: String,
        description: String,
        cardWidth: {
            type: Number,
            default: 205,
        },
        initialSlide: {
            type: Number,
            default: 0,
        },
        wrapAround: {
            type: Boolean,
            default: true,
        },
        snapAlign: {
            type: String,
            default: 'start',
        },
        pagination: {
            type: Boolean,
            default: false,
        },
        cards: {
            type: Array,
            default: [],
        },
    },
    setup(props) {
        const appStore = useAppStore();
        const { sidebarOpen } = storeToRefs(appStore);
        const carouselContainer = ref(null);
        const containerWidth = ref(0);

        const calculateWidth = () => {
            if (carouselContainer.value) {
                containerWidth.value = carouselContainer.value.offsetWidth;
            }
        };

        onMounted(() => {
            window.addEventListener('resize', calculateWidth);
            calculateWidth();
        });

        onBeforeUnmount(() => {
            window.removeEventListener('resize', calculateWidth);
        });

        const spaceNeeded = computed(() => props.numCards * props.cardWidth);

        const showCarousel = computed(
            () => spaceNeeded.value > containerWidth.value
        );

        const config = computed(() => ({
            itemsToShow: 1,
            wrapAround: showCarousel.value && props.wrapAround,
            snapAlign: props.snapAlign,
            itemsToScroll: 1,
            slideSize: props.cardWidth,
            breakpointMode: !sidebarOpen.value ? 'viewport' : 'carousel',
            modelValue: props.initialSlide,
            enabled: showCarousel.value,
            breakpoints: {
                0: {
                    itemsToShow: 1,
                    snapAlign: 'start',
                },
                648: {
                    itemsToShow: 2,
                    snapAlign: 'start',
                },
                1015: {
                    itemsToShow: 3,
                    snapAlign: 'start',
                },
                1200: {
                    itemsToShow: 4,
                    snapAlign: 'start',
                },
            },
        }));

        return {
            carouselContainer,
            showCarousel,
            config,
        };
    },
};
</script>
